import {createApp} from "vue/dist/vue.esm-bundler";
import CardNumber from "./components/inputs/CardNumber.vue"
import InputFormattedExpDate from "./components/InputFormattedExpDate.vue";
import Voucher from "./components/embed/card/Voucher.vue";
import {t} from "./common/langConfig";
import moment from "moment-timezone";
import * as rsa from "js-crypto-rsa";
import WebServiceApi from "./api/web-service-api";

(function (){

    const EXP_DATE_REGEX = /^(\d{2})\/(\d{2})$/g;
    const CVV2_REGEX = /^(\d{3})/g;

    const ecommerce = document.getElementById('ecommerce');
    const formId = ecommerce.dataset.formId || ''
    const config = ecommerce.dataset.config || '{}';

    const PERSON_TYPE = {
        NATURAL_PERSON: 'natural',
        LEGAL_ENTITY: 'legal_entity',
    };

    const PAYMENT_TYPE = {
        VISA: 'VISA',
        MASTERCARD: 'MASTERCARD',
        TELERED: 'TELERED'
    };

    const PAYMENT_SET = new Set(Object.values(PAYMENT_TYPE));

    const WEB_SERVICE_API = new WebServiceApi;

    const app = createApp({
        components: { CardNumber, InputFormattedExpDate, Voucher },
        data() {
            return {
                enablePaymentSelector: false,
                showPaymentFormSelected: false,
                selectedPayment: '',
                showMobile: false,
                currentStep: 1,
                totalSteps: 4,
                validationsPerStep: {},
                resultByStep: {},
                payment: {
                    running: false,
                    finish: false,
                },
                checkout: {
                    pan: null,
                    card_holder: null,
                    exp_date: null,
                    cvv2: null,
                    customer: {
                        name: null,
                        ruc: null,
                        first_surname: null,
                        doc_id_type: '',
                        doc_id: null,
                        type: 'natural',
                        metadata: {
                            email: null,
                            phone: null
                        }

                    },
                    metadatas: {}
                },
                chdErrors: {
                    pan: false,
                    card_holder: false,
                    exp_date: false,
                    cvv2: false,
                },
                customerErrors:{
                    name: false,
                    first_surname: false,
                    doc_id_type: false,
                    doc_id: false,
                    email: false,
                    phone: false,
                    ruc: false,
                },
                message: null,
                transaction: null,
                showVoucher: false,
                showError: false,
                webserviceConfig: {
                    phone: 'PHONE',
                    email: 'EMAIL',
                },
                isErrorPopup: false,
                userClosedPopup: false,
                enableCustomerType: false,
                waitForNotification: false,
            }
        },

        mounted() {
            this.updateStepper();

            if(typeof this.initMixin === 'function'){
                this.initMixin();
            }

            this.validationsPerStep[2] = this.isValidCHDForm;
            this.validationsPerStep[3] = this.isValidCustomerForm;
        },

        provide() {
            // use function syntax so that we can access `this`
            return {
              t: t
            }
        },

        watch: {
            'checkout.card_holder': function(value){
                let validCardholder = value && value.length >= 3 && /^[A-Za-z ]+$/.test(value);
                this.chdErrors.card_holder = !validCardholder;
            },
            'checkout.exp_date': function(value){
                let validExpDate = moment(value, 'MM/YY').isValid();

                if(validExpDate){
                    validExpDate = value.match(EXP_DATE_REGEX) != null;
                }

                let newExpDate = moment('01 / ' + value, 'DD / MM / YY');
                let now = moment().startOf('month');

                if(now > newExpDate){
                    validExpDate = false;
                }

                this.chdErrors.exp_date = !validExpDate;
            },
            'checkout.cvv2': function(value){
                let validateCVV2 = value && value.match(CVV2_REGEX) != null;
                console.log('cvv', validateCVV2);
                this.chdErrors.cvv2 = !validateCVV2;
            },
            'checkout.customer.name': function(value){
                this.validateCustomerNames(value);
            },
            'checkout.customer.ruc': function(value){
                this.validateCustomerRUC(value, this.checkout.customer.type === PERSON_TYPE.LEGAL_ENTITY);
            },
            'checkout.customer.first_surname': function (value) {
                this.validateCustomerNames(value, 'first_surname', this.checkout.customer.type === PERSON_TYPE.NATURAL_PERSON);
            },
            'checkout.customer.doc_id_type': function (value) {
                this.validateCustomerDocType(value);
            },
            'checkout.customer.doc_id': function (value) {
                this.validateCustomerDocId(value);
            },
            'checkout.customer.metadata.email': function (value) {
                this.validateCustomerEmail(value);
            },
            'checkout.customer.metadata.phone': function (value) {
                this.validateCustomerPhone(value);
            },
        },

        computed: {
            canNextStep(){
                let validation = this.validationsPerStep[this.currentStep];
                if(typeof validation === 'function'){
                    let result = validation();
                    this.resultByStep[this.currentStep] = result;
                    return result;
                }
                return false;
            },

            canPay(){
                return Object.values(this.resultByStep)
                .every(val => val === true);
            },

            transactionExists(){
                if(!this.transaction){
                    return false;
                }
                return Object.keys(this.transaction).length > 0;
            },

            isAuthenticatingTransaction(){
                return this.transactionExists && this.transaction.status === 'authenticating';
            },

            isAuthorizedTransaction(){
                return this.transactionExists && this.transaction.status === 'authorized';
            },

            isPendingTransaction(){
                return this.transactionExists && this.transaction.status === 'pending';
            },

            isNaturalPerson(){
                return this.checkout.customer.type === 'natural';
            },

            receiptUrl(){
                if(this.transactionExists){
                    let metadatas = this.transaction.metadatas;

                    if('receipt_url' in metadatas){
                        return metadatas.receipt_url;
                    }
                }
                return false;
            },

            existsNotifiedMetadata(){
                if(!this.transactionExists) return false;

                let metadatas = this.transaction.metadatas;

                return 'notified' in metadatas;
            },

            errorNotification(){
                if(this.existsNotifiedMetadata){
                    let metadatas = this.transaction.metadatas;

                    return metadatas.notified === '-1';
                }
                return false
            },

            getErrorsNotification(){
                if(!this.transactionExists) return [];

                let messages = [];
                try {
                    let metadatas = this.transaction.metadatas;
                    messages = JSON.parse(metadatas.errors_failed_list);
                } catch (error) {
                    console.error(error);
                }

                return messages;
            },

            canShowVoucher(){
                if(this.waitForNotification && this.transactionExists){
                    return this.existsNotifiedMetadata;
                }

                return this.showVoucher && this.transactionExists;
            },

            isSelectedPayment(){
                return PAYMENT_SET.has(this.selectedPayment);
            },

            isSelectedPaymentTelered(){
                return this.selectedPayment === PAYMENT_TYPE.TELERED;
            },
        },

        methods: {
            isValidCHDForm(){
                if(this.enablePaymentSelector){
                    if(this.isSelectedPaymentTelered){
                        return this.showPaymentFormSelected;
                    }
                }

                return this.isValidCHD();
            },

            isValidCustomerForm(){
                //const fields = this.getCustomerFieldsValidate();

                //Revalidate customer fields
                this.validateCustomerNames(this.checkout.customer.name);
                this.validateCustomerNames(this.checkout.customer.name, 'first_surname', this.checkout.customer.type === PERSON_TYPE.NATURAL_PERSON);
                this.validateCustomerDocType(this.checkout.customer.doc_id_type);
                this.validateCustomerDocId(this.checkout.customer.doc_id);
                this.validateCustomerEmail(this.checkout.customer.metadata.email);
                this.validateCustomerPhone(this.checkout.customer.metadata.phone);
                this.validateCustomerRUC(this.checkout.customer.ruc, this.checkout.customer.type === PERSON_TYPE.LEGAL_ENTITY);


                const errFields = Object.values(this.customerErrors);
                const allValid = errFields.every(val => val === false);

                console.log('allValid', allValid);
                if(!allValid){
                    return false;
                }

                return Object.values(this.getCustomerErrors()).every(val => val === false);
            },

            getCustomerFieldsValidate(){
                let fields = [];
                let { customer } = this.checkout;
                let { type, name, metadata } = customer;
                let { email, phone } = metadata;

                if(type === PERSON_TYPE.NATURAL_PERSON){
                    let { first_surname, doc_id_type, doc_id } = customer;

                    fields = [name, first_surname, doc_id_type, doc_id, email, phone];

                }else{
                    fields = [name, email, phone, customer.ruc];
                }

                return fields;
            },

            getCustomerErrors(){
                let { name, first_surname, doc_id_type, doc_id, email, phone, ruc } = this.customerErrors;

                if(this.checkout.customer.type === PERSON_TYPE.NATURAL_PERSON){
                    return { name, first_surname, doc_id_type, doc_id, email, phone };
                }

                return { name, email, phone, ruc };
            },

            nextStep() {
                if (this.currentStep < this.totalSteps) {
                    this.currentStep++;
                    this.updateStepper();
                }
            },

            previousStep() {
                if (this.currentStep > 1) {
                    this.currentStep--;
                    this.updateStepper();
                }
            },

            goToStep(step) {
                if (step >= 1 && step <= this.totalSteps) {
                    this.currentStep = step;
                    this.updateStepper();
                }
            },

            updateStepper() {
                // Actualizar círculos y líneas
                for (let i = 1; i <= this.totalSteps; i++) {
                    const circle = document.getElementById(`step-${i}-circle`);
                    const label = circle.parentElement.querySelector('span');
                    const title = circle.nextElementSibling;

                    if (i < this.currentStep) {
                        // Paso completado
                        circle.className = 'w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold mb-2 step-transition bg-green-600';
                        label.className = 'text-md sm:text-sm text-center text-black-900';
                        title.className = 'text-md sm:text-sm lg:text-lg text-center text-black-900';
                    } else if (i === this.currentStep) {
                        // Paso actual
                        circle.className = 'w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold mb-2 step-transition bg-[#043d70]';
                        label.className = 'text-md sm:text-sm text-center text-white';
                        title.className = 'text-md sm:text-sm lg:text-lg text-center text-black-900';
                    } else {
                        // Paso pendiente
                        circle.className = 'w-10 h-10 rounded-full flex items-center justify-center font-semibold mb-2 step-transition bg-gray-300 text-gray-600';
                        label.className = 'text-md sm:text-sm text-center text-gray-500';
                        title.className = 'text-md sm:text-sm lg:text-lg text-center text-gray-500';
                    }
                }

                // Actualizar líneas conectoras
                for (let i = 1; i < this.totalSteps; i++) {
                    const line = document.getElementById(`line-${i}-${i + 1}`);
                    if (i < this.currentStep) {
                        line.className = 'hidden sm:flex h-full bg-green-600 step-transition';
                    } else {
                        line.className = 'hidden sm:flex h-full bg-gray-300 step-transition';
                    }
                }

                // Mostrar/ocultar contenido
                for (let i = 1; i <= this.totalSteps; i++) {
                    const content = document.getElementById(`step-${i}-content`);
                    if (i === this.currentStep) {
                        content.classList.remove('hidden');
                        content.classList.add('fade-in');
                    } else {
                        content.classList.add('hidden');
                        content.classList.remove('fade-in');
                    }
                }

                // Actualizar botones
                const prevBtn = document.getElementById('prev-btn');
                const nextBtn = document.getElementById('next-btn');
                const finishBtn = document.getElementById('finish-btn');

                prevBtn.disabled = this.currentStep === 1;

                if (this.currentStep === this.totalSteps) {
                    nextBtn.classList.add('hidden');
                    finishBtn.classList.remove('hidden');
                } else {
                    nextBtn.classList.remove('hidden');
                    finishBtn.classList.add('hidden');
                }
            },

            async finish() {
                if(this.isErrorPopup){
                    let { metadatas } = this.transaction;
                    if(metadatas['3ds_authentication_form']){
                        this.initPopup(metadatas['3ds_authentication_form']);
                    }else if(metadatas.place_to_pay_gateway_process_url){
                        this.initPopup(metadatas.place_to_pay_gateway_process_url);
                    }

                    return;
                }

                this.payment.running = true;

                const data = await this.getData(this.checkout);
                this.pay(data);
            },

            async getData(){
                let data = {
                    commerce_id: COMMERCE_ID,
                    customer: await this.getCustomerData(),
                    metadatas: {},
                }

                if(this.isSelectedPaymentTelered){
                    data.card_brand = this.selectedPayment;
                }else{
                    data.pan = await this.encrypt(this.checkout.pan);
                    data.card_holder = await this.encrypt(this.checkout.card_holder);
                    data.exp_date = await this.encrypt(this.checkout.exp_date);
                    data.cvv2 = await this.encrypt(this.checkout.cvv2);
                }

                if(typeof this.addMixinParams === 'function'){
                    data = await this.addMixinParams(data);
                }

                return data;
            },

            async getCustomerData(){
                let { customer } = this.checkout;
                let type = customer.type;

                let data = {
                    name: await this.encrypt(customer.name),
                    type: type,
                    metadata: {
                        email: await this.encrypt(customer.metadata.email),
                        phone: await this.encrypt(customer.metadata.phone)
                    }

                };

                if(type === PERSON_TYPE.NATURAL_PERSON){
                    data.first_surname = await this.encrypt(customer.first_surname);
                    data.doc_id_type = customer.doc_id_type;
                    data.doc_id = await this.encrypt(customer.doc_id);
                }else{
                    data.ruc = await this.encrypt(customer.ruc);
                }

                return data;
            },

            showMessage(message){
                let { reference, status } = message.data;
                let p = '';

                this.message = p;

                if(status === 'pending' || status === 'authenticating'){
                    p = `En estos momentos, su transacción #${reference} se encuentra pendiente de validación por la entidad financiera. Por favor, espere unos minutos a que esta se resuelva.
                        En caso de no recibir una respuesta, le recomendamos ponerse en contacto con nuestra línea de soporte/atención al cliente en ${ this.webserviceConfig.phone } o, alternativamente,
                        puede enviar un correo electrónico a ${this.webserviceConfig.email}.`;
                }else if(status === 'authorized'){
                    p = `Esta factura tiene la transacción con referencia #${reference} aprobada.`;
                }

                this.message = p;

            },

            onCardHolder(event){
                this.checkout.card_holder = (event.target.value).toUpperCase();
            },

            validateCustomerNames(name, errorKey = 'name', require = true) {
                if(!name && !require){
                    this.customerErrors[errorKey] = false;
                }else{
                    let valid = name && name.length >= 3 && /^[A-Za-z ]+$/.test(name);
                    this.customerErrors[errorKey] = !valid;
                }
            },

            validateCustomerDocType(docType, required = false) {
                if (!docType && !required) {
                    this.customerErrors.doc_id_type = false;
                    return;
                }

                let valid = docType && (docType === 'C' || docType === 'P');
                this.customerErrors.doc_id_type = !valid;
            },

            validateCustomerDocId(docId, required = false) {
                if (!docId && !required) {
                    this.customerErrors.doc_id = false;
                    return;
                }

                let valid = false;
                let type = this.checkout.customer.doc_id_type;

                if (type === 'C') {
                    valid = docId && docId.length >= 3 && /^(PE|E|N|[23456789](?:AV|PI)?|1[0123]?(?:AV|PI)?)-?(\d{1,4})-?(\d{1,6})$/.test(docId);
                } else if (type === 'P') {
                    valid = docId && docId.length >= 3 && /^[a-zA-Z0-9_]{4,16}$/.test(docId);
                }

                this.customerErrors.doc_id = !valid;
            },

            validateCustomerEmail(email) {
                let valid = email &&  /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i.test(email);
                this.customerErrors.email = !valid;
            },

            validateCustomerPhone(phone, required = false) {
                if (!phone && !required) {
                    this.customerErrors.phone = false;
                    return;
                }

                let valid = phone  && /^\+[1-9]\d{7,14}$/.test(phone);
                this.customerErrors.phone = !valid;
            },

            validateCustomerRUC(ruc, required = false) {
                if (!ruc && !required) {
                    this.customerErrors.ruc = false;
                    return;
                }

                let valid = ruc && /^[a-zA-Z0-9\-]{1,16}$/.test(ruc);
                this.customerErrors.ruc = !valid;
            },

            onChangeDocIdType(value){
                this.checkout.customer.doc_id = '';
            },

            async encrypt(value) {
                if (ENCRYPTION_ENABLED) {
                    if (value === null || value === undefined) {
                        return null;
                    }

                    value += '';
                    let data = Buffer.from(value, 'utf8');

                    return await rsa.encrypt(data, ENCRYPTION_KEY.keys[0], 'SHA-256')
                        .then(encrypted => {
                            return Buffer.from(encrypted).toString('base64');
                        })
                }

                return value;
            },

            initPopup(url){
                this.payment.finish = true;
                this.payment.running = true;
                // Dimensiones deseadas del popup
                const popupWidth  = 500;
                const popupHeight = 600;
                // Centrar popup en pantalla
                const left = (window.screen.width  - popupWidth)  / 2;
                const top  = (window.screen.height - popupHeight) / 2;

                // Abrir la ventana
                const threeDSPopup = window.open(
                    url,
                    'threeDSChallengeWindow',
                    `width=${popupWidth},height=${popupHeight},top=${top},left=${left},resizable,scrollbars`
                );

                // Si el bloqueador de pop-ups lo impide, window.open() devuelve null
                if (!threeDSPopup) {
                    // Aquí “validas” que no tienes permiso de pop-ups
                    alert('Parece que tu navegador bloqueó la ventana emergente. Por favor, habilita las ventanas emergentes para este sitio y vuelve a intentarlo.');
                    this.isErrorPopup = true;
                    this.payment.finish = false;
                    this.payment.running = false;

                    return;
                }

                this.isErrorPopup = false;
                this.userClosedPopup = false;

                const popupChecker = setInterval(() => {
                    if (!threeDSPopup || threeDSPopup.closed) {
                        clearInterval(popupChecker);
                        // Aquí comentas el cierre
                        console.log('El usuario cerró la ventana.');

                        this.payment.running = false;
                        this.payment.finish = false;

                        alert('No debes cerrar la ventana. Por favor, reintenta el pago.');
                        this.userClosedPopup = true;
                    }
                }, 700);

                window.addEventListener('message', ev => {
                    if (ev.origin !== window.location.origin) return;
                    const msg = ev.data;

                    if(msg.type === '3DS_RESULT'){
                        clearInterval(popupChecker);
                        this.transaction = msg.transaction;

                        if(this.waitForNotification){
                            if(typeof this.getCurrentWSIntegration === 'function'){
                                this.payment.running = true;
                                this.payment.finish = false;
                                this.showVoucher = false;

                                let ws = this.getCurrentWSIntegration();
                                this.transactionDetails(this.transaction.id, ws);
                            }

                        }else{
                            this.payment.running = false;
                            this.payment.finish = true;
                            this.showVoucher = true;
                        }
                    }
                });
            },

            transactionDetails(transactionId, ws){
                WEB_SERVICE_API.transactionDetails(transactionId, ws)
                    .then(response => {
                        let transaction = response.data.data;
                        let metadatas = transaction.metadatas;

                        if('notified' in metadatas){
                            this.showVoucher = true;
                            this.payment.running = false;
                            this.payment.finish = true;

                            this.transaction = transaction;
                        }else{
                            setTimeout(_ => this.transactionDetails(transactionId, ws), 5000);
                        }
                    })
                    .catch(error => {
                        console.error(error);
                    });
            },

            goToStart(){
                globalThis.location.reload();
            },

            selectPayment(payment){
                this.selectedPayment = payment;
            },

            showPaymentForm(){
                this.showPaymentFormSelected = !this.showPaymentFormSelected;
            },

            goToPaymentSelector(){
                this.showPaymentFormSelected = false;
                this.initChdForm();
            },

            initChdForm(){
                this.checkout.pan = null;
                this.checkout.card_holder = null;
                this.checkout.exp_date = null;
                this.checkout.cvv2 = null;

                this.chdErrors = {
                    pan: false,
                    card_holder: false,
                    exp_date: false,
                    cvv2: false,
                };
            },

            isValidCHD(){
                let { pan, card_holder, exp_date, cvv2 } = this.checkout;
                const fields = [pan, card_holder, exp_date, cvv2];

                const allValid = fields.every(val => val !== null && val !== '');

                if(!allValid){
                    return false;
                }

                return Object.values(this.chdErrors).every(val => val === false);
            },
        },

    });

    (async () => {
        try {
            const module = await import(`./forms/ecommerce/${formId}.js`);

            if (typeof module.extendForm === 'function') {
                app.mixin(module.extendForm(formId, config))
            }
        } catch (err) {
            console.warn(`⚠️ No hay extensión para el formulario "${formId}"`, err)
        }

        app.mount('#ecommerce');
    })();

})();
